import { PropsWithChildren, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import Moment from 'react-moment';
import styled from 'styled-components';
import moment from 'moment';
import { useLocation } from 'react-router';

import {
	Breakpoint,
	Button,
	Color,
	ContextMenu,
	IconColor,
	Paragraph,
	Spinner,
} from '@treams/treams-ui';
import {
	ArchiveIcon,
	CalendarIcon,
	ChevronDownIcon,
	FriendsIcon,
	PrintIcon,
	SyncIcon,
	WarningIcon,
} from '@treams/treams-icons';

import * as graphql from 'generated/graphql';

import EvaluationSubmissionShareModal from './EvaluationSubmissionShareModal';
import EvaluationSubmissionSignatureModal from './EvaluationSubmissionSignatureModal';
import ChangeConversationPartnerModal from './ChangeConversationPartnerModal';
import ChangeConversationDateModal from './ChangeConversationDateModal';
import { Route, useHistory } from '@treams/treams-routes';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';

const Footer = styled(Box)<{ isSidebarOpen: boolean }>`
	border-top: 1px solid ${(props) => props.theme.colors.lightBlue};
	position: fixed;
	bottom: 0;
	right: 0;
	left: 0;
	background: #fff;

	@media screen and (min-width: ${Breakpoint.Medium}) {
		left: 270px;
	}

	transition: 500ms cubic-bezier(0, 1, 0, 1) padding-right;
	transition-delay: 300ms;

	padding-right: 80px;
	@media screen and (min-width: ${Breakpoint.Medium}) {
		padding-right: ${(props) => (props.isSidebarOpen ? '300' : '80')}px;
	}
`;

function ConversationActionsButton(props: {
	submission: graphql.EvaluationSubmission;
	onChangeDate: () => void;
	onChangePartner: () => void;
}) {
	const location = useLocation();
	const { t } = useTranslation();
	const [archive, { loading: archiveLoading }] = graphql.useArchiveEvaluationSubmissionMutation({
		variables: { input: { id: props.submission.id } },
	});
	const [restore, { loading: restoreLoading }] = graphql.useRestoreEvaluationSubmissionMutation({
		variables: { input: { id: props.submission.id } },
	});

	return (
		<ContextMenu.Target
			menu={
				<ContextMenu width={260}>
					{props.submission.viewerCanChangeConversationPartner && (
						<ContextMenu.Item
							title={t('evaluation.submission.changeConversationPartner.button')}
							icon={<FriendsIcon />}
							onClick={() => props.onChangePartner()}
						/>
					)}

					<ContextMenu.Item
						title={t('evaluation.submission.changeDate.button')}
						icon={<CalendarIcon />}
						onClick={() => props.onChangeDate()}
					/>

					<a href={`${location.pathname}/print`} target="_blank" rel="noreferrer">
						<ContextMenu.Item title={t('evaluation.submission.print')} icon={<PrintIcon />} />
					</a>

					{props.submission.viewer.viewerCanArchive && (
						<>
							<ContextMenu.Separator />
							<ContextMenu.Item
								title={
									archiveLoading
										? t('evaluation.submission.archiveRestore.archiving')
										: restoreLoading
										? t('evaluation.submission.archiveRestore.restoring')
										: props.submission.isArchived
										? t('evaluation.submission.archiveRestore.restore')
										: t('evaluation.submission.archiveRestore.archive')
								}
								icon={<ArchiveIcon />}
								type="destructive"
								onClick={async () => {
									props.submission.isArchived ? await restore() : await archive();
								}}
							/>
						</>
					)}
				</ContextMenu>
			}
		>
			<Flex flexShrink={0}>
				<Button
					small
					title={t('evaluation.submission.actions.button')}
					mr={2}
					type="secondary"
					rightIcon={<ChevronDownIcon />}
				/>
			</Flex>
		</ContextMenu.Target>
	);
}

function ConversationSaveStatus(
	props: PropsWithChildren<{
		submission: graphql.EvaluationSubmission;
		saving: boolean;
		error?: Error;
		save: () => Promise<void>;
		icon: ReactNode;
	}>,
) {
	const { t } = useTranslation();

	return (
		<Flex alignItems="center">
			<Box mr={2} size="16px">
				{props.saving ? (
					<Spinner color={props.error ? 'red' : undefined} />
				) : (
					<IconColor color={props.error ? 'red' : 'green'}>{props.icon}</IconColor>
				)}
			</Box>
			<Paragraph
				display={['none', 'none', 'block', 'block']}
				color={props.error ? 'red' : 'slateBlue'}
			>
				{props.children}
			</Paragraph>
			<Box ml={3}>
				{props.submission.viewer.viewerCanUpdate && props.error && (
					<Color color="red">
						<Button
							title={t('evaluations.submission.autosave.tryAgain')}
							onClick={() => {
								props.save();
							}}
							type="plain"
							small
						/>
					</Color>
				)}
			</Box>
		</Flex>
	);
}

export default function ConversationFooter(props: {
	submission: graphql.EvaluationSubmission;
	attempt: <F extends Function>(fn: F) => F;
	save: () => Promise<void>;
	acquire: () => Promise<void>;
	onErrors: (errors: Set<string>) => void;
	error?: Error;
	saving: boolean;
	lastSaved: Date;
	isSidebarOpen: boolean;
}) {
	const { t } = useTranslation();
	const [shareModal, setShareModal] = useState(false);
	const [signModal, setSignModal] = useState(false);
	const [changePartnerModal, setChangePartnerModal] = useState(false);

	const history = useHistory();
	const viewer = useAuthenticatedUser();

	return (
		<Footer py={2} isSidebarOpen={props.isSidebarOpen}>
			{shareModal && (
				<EvaluationSubmissionShareModal
					submission={props.submission}
					attempt={props.attempt}
					onDismiss={() => setShareModal(false)}
				/>
			)}
			{signModal && (
				<EvaluationSubmissionSignatureModal
					submission={props.submission}
					saving={props.saving}
					acquire={props.acquire}
					onErrors={props.onErrors}
					onDismiss={() => setSignModal(false)}
				/>
			)}
			<Route
				path={[`/users/:userID/conversations/:id/change-date`, `/conversations/:id/change-date`]}
			>
				{({ match }) =>
					!!match && (
						<ChangeConversationDateModal
							id={props.submission.id}
							deadline={props.submission.deadline}
							duration={props.submission.meeting?.duration}
							location={props.submission.meeting?.location}
							onDismiss={() =>
								history.push(
									`/v2/users/${props.submission.employee.user.id}/conversations/${props.submission.id}`.replace(
										`/users/${viewer.id}`,
										'',
									),
								)
							}
						/>
					)
				}
			</Route>
			{changePartnerModal && (
				<ChangeConversationPartnerModal
					id={props.submission.id}
					employeeID={props.submission.employee.user.id}
					userID={props.submission.manager.user.id}
					enabled={props.submission.viewerCanChangeConversationPartner}
					onDismiss={() => setChangePartnerModal(false)}
				/>
			)}

			<Flex justifyContent="space-between" alignItems="center" pl={3}>
				{props.submission.viewer.viewerCanArchive && (
					<>
						{!props.error && (
							<ConversationSaveStatus
								save={props.save}
								saving={props.saving}
								icon={<SyncIcon />}
								submission={props.submission}
							>
								{props.submission.viewer.hasShared
									? t('evaluations.submission.autosave.submission')
									: t('evaluations.submission.autosave.concept')}
								&nbsp;
								<Moment interval={5000} fromNow>
									{moment(props.lastSaved)}
								</Moment>
							</ConversationSaveStatus>
						)}

						{props.error && (
							<ConversationSaveStatus
								save={props.save}
								saving={props.saving}
								error={props.error}
								icon={<WarningIcon />}
								submission={props.submission}
							>
								{t('evaluations.submission.autosave.error')}
							</ConversationSaveStatus>
						)}
					</>
				)}

				<Flex mr={3}>
					<ConversationActionsButton
						submission={props.submission}
						onChangeDate={() =>
							history.push(
								`/v2/users/${props.submission.employee.user.id}/conversations/${props.submission.id}/change-date`.replace(
									`/users/${viewer.id}`,
									'',
								),
							)
						}
						onChangePartner={() => {
							setChangePartnerModal(true);
						}}
					/>

					{props.submission.viewer.viewerCanShare && (
						<Button
							small
							title={t('evaluation.submission.save.share')}
							onClick={() => {
								setShareModal(true);
							}}
							flexShrink={0}
						/>
					)}

					{props.submission.viewer.viewerCanSign && (
						<Button
							small
							title={t('evaluation.submission.confirm.button')}
							onClick={() => {
								setSignModal(true);
							}}
							flexShrink={0}
						/>
					)}
				</Flex>
			</Flex>
		</Footer>
	);
}
